import React from 'react'
import { Controller, FormContext, useForm } from 'react-hook-form'
import { Info as InfoIcon } from 'react-feather'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import {
  Grid,
  useMediaQuery,
  Typography,
  Box,
  FormControlLabel,
  Switch,
  IconButton,
  Tooltip,
  FormControl,
  RadioGroup,
  FormLabel,
  Radio,
  FormHelperText,
} from '@material-ui/core'

import { ColorSpecification, PreviewPage } from 'components'

import helpers from 'helpers'

import useSnackbar from 'hooks/useSnackbar'

import theme from 'theme'
import schema from './schema'
import useStyles from './styles'
import * as service from 'service'
import { Header } from './components'
import tours from 'constants/tours'
import constants from 'constants/index'

const SettingsForm = ({ portalTheme, refresh, loading, setLoading }) => {
  const { WHITE_LABEL_COMPANY_LOGO, WHITE_LABEL_LOGO_CONTROL_ITEMS } =
    constants.privacyStamps

  const classes = useStyles()
  const snackbar = useSnackbar()
  const isExtraLarge = useMediaQuery(theme.breakpoints.up('lg'), {
    defaultMatches: true,
  })

  const formMethods = useForm({
    validationSchema: schema,
    defaultValues: {
      primaryColor: portalTheme?.primaryColor || theme.palette.primary.main,
      primaryFontColor:
        portalTheme?.primaryFontColor || theme.palette.common.white,
      secondaryColor:
        portalTheme?.secondaryColor || theme.palette.primary.hover,
      secondaryFontColor:
        portalTheme?.secondaryFontColor || theme.palette.common.white,
      applyStamp: portalTheme?.applyStamp || false,
      stampKind: portalTheme?.stampKind || WHITE_LABEL_COMPANY_LOGO,
      pageTitle: portalTheme?.pageTitle || '',
      pageSubtitle: portalTheme?.pageSubtitle || '',
    },
  })

  const { control, handleSubmit, watch, errors } = formMethods

  const applyStamp = watch('applyStamp')
  const stampKind = watch('stampKind')

  const { mixpanel } = helpers

  const mixpanelTrack = () => {
    mixpanel.track('Personalização do Portal da Privacidade', 'Personalizar', {
      action: 'salvar_config_canal',
    })
  }

  const onSubmit = async (data) => {
    try {
      setLoading(true)

      await service.dponet.privacyPortal.postTheme({ ...data })
      snackbar.open({
        message: 'Tema salvo com sucesso!',
        variant: 'success',
      })
      refresh()
      mixpanelTrack()
    } catch (error) {
      console.error(error)
      snackbar.open({
        message: helpers.formatters.errorMessage(error),
        variant: 'error',
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <FormContext {...formMethods}>
      <form id="privacy-portal-theme-form" onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={isExtraLarge ? 10 : 6} py={6} pl={6}>
          <Grid
            item
            xs={12}
            md={5}
            id={tours.privacyPortalTheme.MAIN_STEP_2}
            component={Box}
            display="flex"
            flexDirection="column"
            gridGap={theme.spacing(3)}
          >
            <Header />
            <ColorSpecification
              titleName="Especificação da primeira cor"
              subtitleName="(Cabeçalho e rodapé)"
              buttonMainName="Primeira Cor"
              buttonFontName="Primeira Fonte"
              controlMainName="primaryColor"
              controlFontName="primaryFontColor"
            />
            <ColorSpecification
              titleName="Especificação da segunda cor"
              subtitleName="(Cards, botões)"
              buttonMainName="Segunda Cor"
              buttonFontName="Segunda Fonte"
              controlMainName="secondaryColor"
              controlFontName="secondaryFontColor"
            />
            <Box
              display="flex"
              flexDirection="column"
              gridGap={theme.spacing(1)}
            >
              <Box
                display="flex"
                alignItems="center"
                gridGap={theme.spacing(1)}
              >
                <Typography variant="h6">Configurações de aplicação</Typography>
                <Tooltip
                  title="Locais adicionais onde o tema será aplicado."
                  placement="right"
                >
                  <IconButton size="small">
                    <InfoIcon size={16} color={theme.palette.info.dark} />
                  </IconButton>
                </Tooltip>
              </Box>
              <Box
                display="flex"
                flexDirection="column"
                gridGap={theme.spacing(2)}
              >
                <Box>
                  <Controller
                    as={
                      <FormControl
                        className={clsx({
                          [classes.errorText]: !!errors?.applyStamp,
                        })}
                      >
                        <FormControlLabel
                          label="Selo para impressão"
                          control={
                            <Switch color="primary" checked={applyStamp} />
                          }
                        />
                        {!!errors?.applyStamp && (
                          <FormHelperText error={!!errors?.applyStamp}>
                            {errors?.applyStamp?.message}
                          </FormHelperText>
                        )}
                      </FormControl>
                    }
                    control={control}
                    name="applyStamp"
                  />
                </Box>
                {applyStamp && (
                  <Controller
                    as={
                      <FormControl
                        className={clsx({
                          [classes.errorText]: !!errors?.stampKind,
                        })}
                      >
                        <FormLabel
                          id="privacy-stamps-radio-group-label"
                          className={clsx({
                            [classes.errorText]: !!errors?.stampKind,
                          })}
                        >
                          Escolha qual logotipo devera ser apresentado no selo
                        </FormLabel>
                        <RadioGroup
                          aria-labelledby="privacy-stamps-radio-group-label"
                          name="privacy-stamps-radio-group"
                          value={stampKind}
                          row
                        >
                          {WHITE_LABEL_LOGO_CONTROL_ITEMS.map((item, index) => (
                            <FormControlLabel
                              key={index}
                              value={item.value}
                              control={
                                <Radio
                                  size="small"
                                  className={clsx({
                                    [classes.errorText]: !!errors?.stampKind,
                                  })}
                                />
                              }
                              label={item.label}
                            />
                          ))}
                        </RadioGroup>
                        {!!errors?.stampKind && (
                          <FormHelperText error={!!errors?.stampKind}>
                            {errors?.stampKind?.message}
                          </FormHelperText>
                        )}
                      </FormControl>
                    }
                    control={control}
                    name="stampKind"
                  />
                )}
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} md={7}>
            <PreviewPage />
          </Grid>
        </Grid>
      </form>
    </FormContext>
  )
}

SettingsForm.propTypes = {
  portalTheme: PropTypes.shape({
    primaryColor: PropTypes.string,
    primaryFontColor: PropTypes.string,
    secondaryColor: PropTypes.string,
    secondaryFontColor: PropTypes.string,
  }),
  refresh: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  setLoading: PropTypes.func.isRequired,
}

export default SettingsForm
